import { useEffect, useState } from "react";
import { CalendarDays } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

// The portal writes a date as dd.mm.yyyy and holds a compliance day as yyyy-mm-dd. One control
// reads and writes both, so a field never has to care which one it is keeping.
const DOTTED = /^(\d{1,2})[.\-/](\d{1,2})[.\-/](\d{4})$/;
const ISO = /^(\d{4})-(\d{2})-(\d{2})/;

const pad = (n) => String(n).padStart(2, "0");

export const toDotted = (value) => {
  const text = String(value || "").trim();
  const iso = ISO.exec(text);
  if (iso) return `${iso[3]}.${iso[2]}.${iso[1]}`;
  const dot = DOTTED.exec(text);
  return dot ? `${pad(dot[1])}.${pad(dot[2])}.${dot[3]}` : text;
};

export const toIso = (value) => {
  const text = String(value || "").trim();
  const iso = ISO.exec(text);
  if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;
  const dot = DOTTED.exec(text);
  return dot ? `${dot[3]}-${pad(dot[2])}-${pad(dot[1])}` : "";
};

const asDate = (value) => {
  const iso = toIso(value);
  if (!iso) return undefined;
  const d = new Date(`${iso}T00:00:00`);
  return Number.isNaN(d.getTime()) ? undefined : d;
};

/**
 * The portal's one date control.
 *
 * Every date field in the portal is this: a calendar an officer presses, and the same box still
 * open to typing for those who are faster at it. `format="iso"` keeps yyyy-mm-dd — what the
 * compliance store holds — and everything else keeps the register's own dd.mm.yyyy.
 */
export const DayField = ({
  value = "",
  onChange,
  testId,
  format = "dotted",
  placeholder,
  className = "",
  disabled,
  min,
  max,
  tall = false,
}) => {
  const [typed, setTyped] = useState(() => toDotted(value));
  const [open, setOpen] = useState(false);

  useEffect(() => setTyped(toDotted(value)), [value]);

  const give = (next) => onChange?.(format === "iso" ? toIso(next) || "" : toDotted(next));

  return (
    <div className={`relative ${className}`}>
      <Input
        data-testid={testId}
        value={typed}
        disabled={disabled}
        placeholder={placeholder || "dd.mm.yyyy"}
        inputMode="numeric"
        onChange={(e) => {
          setTyped(e.target.value);
          const iso = toIso(e.target.value);
          if (iso) give(e.target.value);
          else if (!e.target.value.trim()) onChange?.("");
        }}
        onBlur={() => setTyped(toDotted(value))}
        className={`font-data ${tall ? "h-11" : "h-10"} rounded-none pr-9`}
      />
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            disabled={disabled}
            data-testid={testId ? `${testId}-calendar` : undefined}
            aria-label="Open the calendar"
            className={`absolute right-0 top-0 flex ${
              tall ? "h-11" : "h-10"
            } w-9 items-center justify-center text-muted-foreground transition-colors duration-200 hover:text-primary disabled:opacity-40`}
          >
            <CalendarDays className="h-4 w-4" />
          </button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-auto rounded-none bg-popover p-0">
          <Calendar
            mode="single"
            selected={asDate(value)}
            defaultMonth={asDate(value)}
            disabled={
              min || max
                ? (day) =>
                    (min && day < new Date(`${toIso(min)}T00:00:00`)) ||
                    (max && day > new Date(`${toIso(max)}T00:00:00`))
                : undefined
            }
            onSelect={(day) => {
              if (!day) return;
              give(`${day.getFullYear()}-${pad(day.getMonth() + 1)}-${pad(day.getDate())}`);
              setOpen(false);
            }}
          />
          <div className="flex items-center justify-between gap-2 border-t border-border px-3 py-2">
            <button
              type="button"
              data-testid={testId ? `${testId}-today` : undefined}
              onClick={() => {
                const now = new Date();
                give(`${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`);
                setOpen(false);
              }}
              className="font-display text-[10px] font-bold uppercase tracking-widest text-primary"
            >
              Today
            </button>
            <button
              type="button"
              data-testid={testId ? `${testId}-clear` : undefined}
              onClick={() => {
                onChange?.("");
                setTyped("");
                setOpen(false);
              }}
              className="font-display text-[10px] font-bold uppercase tracking-widest text-muted-foreground"
            >
              Clear it
            </button>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};
